import { useEffect, useState, useCallback } from 'react';

import { RECAPTCHA_RENDER_URL } from 'constants/signup-flow-constants';

import useCSRFToken from './use-csrf-token';

const reCaptchaPublicKey = process.env.RECAPTCHA_PUBLIC_KEY;

interface UseRecaptchaHandler {
  reCaptchaLoaded: boolean;
  validateRecaptcha: (
    action: string,
    minScore?: number,
  ) => Promise<{ success: boolean }>;
  passedTest: boolean | undefined;
  error: string | null;
}

type RecaptchaValidateResponseType = {
  success: boolean;
  error?: string;
};

const useRecaptcha = (): UseRecaptchaHandler => {
  const [reCaptchaLoaded, setReCaptchaLoaded] = useState<boolean>(false);
  const [passedTest, setPassedTest] = useState<boolean | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const csrfToken = useCSRFToken();

  useEffect(() => {
    if (typeof window === 'undefined' || reCaptchaLoaded) return;
    if (window.grecaptcha && !reCaptchaLoaded) {
      setReCaptchaLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.async = true;
    script.src = RECAPTCHA_RENDER_URL;
    script.onload = () => {
      setReCaptchaLoaded(true);
    };
    script.onerror = () => {
      setReCaptchaLoaded(false);
    };
    document.body.appendChild(script);
  }, [reCaptchaLoaded]);

  const generateReCaptchaToken = useCallback(
    (action: string): Promise<string> => {
      // eslint-disable-next-line consistent-return
      return new Promise((resolve, reject) => {
        if (!reCaptchaLoaded)
          return reject(new Error('ReCaptcha not initialized (yet)'));
        if (typeof window === 'undefined' || !window.grecaptcha) {
          setReCaptchaLoaded(false);
          return reject(new Error('ReCaptcha not loaded'));
        }
        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(reCaptchaPublicKey as string, { action })
            .then((token: string) => {
              resolve(token);
            });
        });
      });
    },
    [reCaptchaLoaded],
  );

  const validateReCaptchaToken = useCallback(
    async (
      token: string,
      minScore: number,
    ): Promise<RecaptchaValidateResponseType> => {
      const response = await fetch('/api/recaptcha/validate/', {
        body: JSON.stringify({ recaptcha_token: token }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        method: 'POST',
      });
      try {
        const data = await response.json();
        if (data.success) {
          if (data.score >= minScore) {
            setPassedTest(true);
            return { success: data.success };
          }
          setPassedTest(false);
          setError('Recaptcha score too low');
          return { success: false };
        }
        setPassedTest(false);
        setError(data.error);
        throw new Error(data.error);
      } catch {
        setError('Recaptcha validation failed');
        throw new Error('Recaptcha validation failed');
      }
    },
    [csrfToken],
  );

  const validateRecaptcha = useCallback(
    async (action: string, minScore: number = 0.7) => {
      try {
        const token = await generateReCaptchaToken(action);
        const response = await validateReCaptchaToken(token, minScore);
        return { success: response.success };
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
        return { success: false };
      }
    },
    [generateReCaptchaToken, validateReCaptchaToken],
  );

  return { error, passedTest, reCaptchaLoaded, validateRecaptcha };
};

export default useRecaptcha;
